import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Button, Spinner } from '@timely/ui';
import { supabase } from '../lib/supabaseClient';
import { useSession } from '../auth/SessionProvider';
import { useWorkspaces } from '../workspace/WorkspaceProvider';
import { AuthLayout, AuthLink } from './AuthLayout';

/**
 * Accepting an invitation.
 *
 * The work is all in `accept_invite()` (migration 0004), which is where it has
 * to be: the token is matched against a stored digest, the invitation is bound
 * to the address it was sent to, it is good once, and it expires. Holding the
 * link is not enough — a forwarded email does not become a membership. This
 * screen carries the token there and reports what it said.
 *
 * Signed out, the token is kept in the URL through sign-up or sign-in so it is
 * still here afterwards.
 */
export function AcceptInvite() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { state, loading, verified } = useSession();
    const { reload } = useWorkspaces();
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const attempted = useRef(false);

    useEffect(() => {
        if (loading || state !== 'active' || !verified || !token || attempted.current) return;
        attempted.current = true;
        setBusy(true);
        void supabase()
            .rpc('accept_invite', { p_token: token })
            .then(async ({ data, error: rpcError }) => {
                setBusy(false);
                if (rpcError) {
                    // These messages are written for the person: "sent to a
                    // different address", "already been used", "has expired".
                    setError(rpcError.message || 'That invitation could not be accepted.');
                    return;
                }
                await reload();
                navigate(data ? '/' : '/', { replace: true });
            });
    }, [loading, state, verified, token, navigate, reload]);

    if (!token) return <Navigate to="/" replace />;

    if (loading) {
        return (
            <div className="min-h-dvh grid place-items-center" role="status" aria-live="polite">
                <span className="sr-only">Checking your invitation</span>
                <Spinner className="text-accent w-6 h-6" />
            </div>
        );
    }

    if (state !== 'active') {
        return (
            <AuthLayout
                title="You have been invited"
                lede="Sign in with the address the invitation was sent to, and you will join straight away."
                footer={<>No account yet? <AuthLink to={`/signup?invite=${encodeURIComponent(token)}`}>Create one</AuthLink></>}
            >
                <Button
                    variant="primary"
                    className="w-full"
                    onClick={() => navigate(`/login?returnTo=${encodeURIComponent(`/invite/${token}`)}`)}
                >
                    Sign in to accept
                </Button>
            </AuthLayout>
        );
    }

    if (!verified) return <Navigate to="/verify-email" replace />;

    if (error) {
        return (
            <AuthLayout
                title="That invitation cannot be used"
                lede={error}
                footer={<AuthLink to="/">Go to your companies</AuthLink>}
            >
                <p className="text-[13.5px] text-label-2 leading-relaxed">
                    Invitations are sent to one address and can be used once. Ask whoever invited
                    you to send another.
                </p>
            </AuthLayout>
        );
    }

    return (
        <div className="min-h-dvh grid place-items-center" role="status" aria-live="polite">
            <span className="sr-only">{busy ? 'Accepting your invitation' : 'Please wait'}</span>
            <Spinner className="text-accent w-6 h-6" />
        </div>
    );
}
